import { useMemo } from 'react';
import { Button, Group, Paper, Stack, Text } from '@mantine/core';
import { FormattedMessage, useIntl } from 'react-intl';

import { CommodityCard } from './CommodityCard';
import { freightFormMessages } from './freight-form-messages';
import { PACKAGING_TYPE_OPTIONS } from '../../../../ltl/constants';
import { CustomNumberInput, DimensionsInput } from '../../../common';
import { Dropdown } from '../../../common/Dropdown';

import type { Dimensions } from '../../../../types/dimensions';
import type { FreightPackagingType, LtlCommodity, LtlHandlingUnit } from '../../../../types/ltl';

type HandlingUnitCardProps = {
  unit: LtlHandlingUnit;
  index: number;
  onChange: (updated: LtlHandlingUnit) => void;
  onRemove?: () => void;
};

const createEmptyCommodity = (existingCommodities: LtlCommodity[]): LtlCommodity => ({
  id: crypto.randomUUID(),
  description: '',
  freightClass: '',
  nmfcCode: '',
  quantity: 1,
  weight: 0,
  weightUnit: existingCommodities[0]?.weightUnit,
  isHazmat: false,
  hazmatDetails: null,
  insurance: { isInsured: false, commodityCategoryCode: '', condition: '', declaredValue: 0 },
});

export const HandlingUnitCard = ({ unit, index, onChange, onRemove }: HandlingUnitCardProps) => {
  const intl = useIntl();

  const handleTypeChange = (val: string | null) => {
    if (val) onChange({ ...unit, packagingType: val as FreightPackagingType });
  };

  const handleQuantityChange = (val: number | string) => {
    const num = typeof val === 'string' ? parseInt(val, 10) || 1 : val;
    onChange({ ...unit, quantity: num });
  };

  const handleStackableChange = (val: string | null) => {
    onChange({ ...unit, isStackable: val === 'yes' });
  };

  const handleDimensionsChange = (next: Dimensions) => {
    onChange({ ...unit, length: next.length, width: next.width, height: next.height });
  };

  const handleCommodityChange = (commodityIndex: number, updated: LtlCommodity) => {
    const commodities = [...unit.commodities];
    commodities[commodityIndex] = updated;
    onChange({ ...unit, commodities });
  };

  const handleAddCommodity = () => {
    onChange({ ...unit, commodities: [...unit.commodities, createEmptyCommodity(unit.commodities)] });
  };

  const handleRemoveCommodity = (commodityIndex: number) => {
    onChange({ ...unit, commodities: unit.commodities.filter((_, idx) => idx !== commodityIndex) });
  };

  const dimensionsValue: Dimensions = {
    length: unit.length,
    width: unit.width,
    height: unit.height,
    unit: (unit.dimensionUnit as Dimensions['unit']) ?? 'Inches',
  };

  const packagingTypeData = useMemo(
    () =>
      PACKAGING_TYPE_OPTIONS.map((option) => ({
        value: option.value,
        label: intl.formatMessage(option.label),
      })),
    [intl],
  );

  return (
    <Paper data-testid={`handling-unit-card-${index}`} p='md' shadow='xs' withBorder>
      <Stack gap='sm'>
        <Group justify='space-between'>
          <Text fw={600}>{intl.formatMessage(freightFormMessages.handlingUnitTitle, { number: index + 1 })}</Text>
          {onRemove ? (
            <Button color='red' data-testid={`remove-unit-${index}`} onClick={onRemove} size='xs' variant='ghost'>
              <FormattedMessage {...freightFormMessages.removeUnit} />
            </Button>
          ) : null}
        </Group>
        <Group grow>
          <Dropdown
            data={packagingTypeData}
            data-testid={`unit-type-${index}`}
            label={intl.formatMessage(freightFormMessages.unitTypeLabel)}
            onChange={handleTypeChange}
            value={unit.packagingType}
          />
          <CustomNumberInput
            allowNegative={false}
            data-testid={`unit-quantity-${index}`}
            label={intl.formatMessage(freightFormMessages.quantityLabel)}
            min={1}
            onChange={handleQuantityChange}
            value={unit.quantity}
          />
          <Dropdown
            data={[
              { value: 'yes', label: intl.formatMessage(freightFormMessages.stackableYes) },
              { value: 'no', label: intl.formatMessage(freightFormMessages.stackableNo) },
            ]}
            data-testid={`unit-stackable-${index}`}
            label={intl.formatMessage(freightFormMessages.stackableLabel)}
            onChange={handleStackableChange}
            value={unit.isStackable ? 'yes' : 'no'}
          />
        </Group>
        <Text size='sm'>{intl.formatMessage(freightFormMessages.dimensionsLabel, { unit: dimensionsValue.unit })}</Text>
        <DimensionsInput onChange={handleDimensionsChange} value={dimensionsValue} />
        {unit.commodities.map((commodity, cIdx) => (
          <CommodityCard
            commodity={commodity}
            index={cIdx}
            key={commodity.id}
            onChange={(updated) => handleCommodityChange(cIdx, updated)}
            onRemove={cIdx > 0 ? () => handleRemoveCommodity(cIdx) : undefined}
          />
        ))}
        <Button color='blue.7' data-testid={`add-commodity-${index}`} onClick={handleAddCommodity} size='xs' variant='ghost'>
          <FormattedMessage {...freightFormMessages.addCommodity} />
        </Button>
      </Stack>
    </Paper>
  );
};
